
import { animate, style, transition, trigger } from '@angular/animations';
import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  inject,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import {
  MatPaginator,
  MatPaginatorModule,
  PageEvent,
} from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterModule } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { PracticeArea } from '../../../models/user.models';
import { AuthService } from '../../../services/auth.service';
import { FeatureFlagService } from '../../../services/feature-flag.service';
import { PracticeAreaService } from '../../../services/practice-area.service';
import { SnackbarService } from '../../../services/snackbar.service';
import { describeApiError } from '../../../utils/api-error.utils';
import { ErrorStateComponent } from '../../shared/error-state/error-state.component';

@Component({
  selector: 'app-practice-areas',
  standalone: true,
  imports: [
    FormsModule,
    MatButtonModule,
    MatCardModule,
    MatChipsModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
    MatSortModule,
    MatTableModule,
    MatTooltipModule,
    RouterModule,
    ErrorStateComponent
],
  templateUrl: './practice-areas.component.html',
  styleUrls: ['./practice-areas.component.scss'],
  animations: [
    // The "New Practice Area" button and the name field occupy the same right-aligned
    // slot in the page header, so animating both widths at once reads as the button
    // widening leftward into the field (and contracting back on cancel).
    trigger('expandCreateControl', [
      transition(':enter', [
        style({ width: 0, opacity: 0 }),
        animate(
          '220ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({ width: '*', opacity: 1 }),
        ),
      ]),
      transition(':leave', [
        animate(
          '180ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({ width: 0, opacity: 0 }),
        ),
      ]),
    ]),
  ],
})
export class PracticeAreasComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private practiceAreaService = inject(PracticeAreaService);
  private authService = inject(AuthService);
  private featureFlagService = inject(FeatureFlagService);
  private snackbarService = inject(SnackbarService);
  private router = inject(Router);

  readonly sort = viewChild(MatSort);
  readonly paginator = viewChild(MatPaginator);
  readonly newPracticeAreaInput =
    viewChild<ElementRef<HTMLInputElement>>('newPracticeAreaInput');
  readonly createTrigger = viewChild<ElementRef<HTMLElement>>('createTrigger');

  private destroy$ = new Subject<void>();

  practiceAreas: PracticeArea[] = [];
  filteredPracticeAreas: PracticeArea[] = [];
  isLoading = false;
  error: string | null = null;
  sortField = 'description';
  sortDirection: 'asc' | 'desc' = 'asc';

  // Material table properties
  displayedColumns: string[] = ['practiceCode', 'description'];
  dataSource = new MatTableDataSource<PracticeArea>([]);

  // Pagination
  totalCount = 0;
  pageSize = 10;
  pageIndex = 0;
  pageSizeOptions = [5, 10, 25];

  // Search
  searchQuery = '';
  private searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  // Inline create control
  readonly maxNameLength = 200;
  /**
   * Gates the create control on the `custom-practice-areas` flag. Starts false and is only raised
   * once the flag is confirmed enabled, so a failed or slow flag lookup hides the control rather
   * than offering a create the API cannot yet complete.
   */
  customPracticeAreasEnabled = false;
  /**
   * Suppresses the expand animation for the first render, so the button appears with the
   * rest of the header instead of widening into place on every visit to the page. Stays off
   * for the whole page when the user prefers reduced motion.
   */
  animationsEnabled = false;
  isCreating = false;
  newPracticeAreaName = '';
  isSavingNewPracticeArea = false;
  /**
   * Why the last create attempt was rejected, or null if none was. Shown only to assistive tech,
   * via an `aria-describedby` target — the toast is what puts the same text on screen.
   */
  createErrorMessage: string | null = null;
  /**
   * Transient — drives the wiggle, cleared when the animation finishes. Stays false under reduced
   * motion, where no animation runs and so nothing would arrive to clear it.
   */
  isWiggling = false;
  private focusTimer: ReturnType<typeof setTimeout> | null = null;
  private wiggleFrame: number | null = null;
  private isDestroyed = false;

  ngOnInit(): void {
    this.featureFlagService
      .isFeatureFlagEnabled('custom-practice-areas')
      .pipe(takeUntil(this.destroy$))
      .subscribe((enabled) => (this.customPracticeAreasEnabled = enabled));

    this.loadPracticeAreas();
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;

    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
    if (this.focusTimer) {
      clearTimeout(this.focusTimer);
    }
    if (this.wiggleFrame !== null) {
      cancelAnimationFrame(this.wiggleFrame);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  private applyFilter(searchTerm: string): void {
    const term = searchTerm.toLowerCase().trim();

    if (!term) {
      this.filteredPracticeAreas = [...this.practiceAreas];
    } else {
      this.filteredPracticeAreas = this.practiceAreas.filter(
        (pa) =>
          pa.practiceCode.toLowerCase().includes(term) ||
          pa.description.toLowerCase().includes(term),
      );
    }

    // Apply current sort
    this.applySortToFiltered();

    this.totalCount = this.filteredPracticeAreas.length;
    this.pageIndex = 0;
    this.updateDataSource();
  }

  private updateDataSource(): void {
    const startIndex = this.pageIndex * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.dataSource.data = this.filteredPracticeAreas.slice(
      startIndex,
      endIndex,
    );
  }

  onPageChange(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this.updateDataSource();
  }

  loadPracticeAreas(): void {
    this.isLoading = true;
    this.error = null;

    this.practiceAreaService
      .getAllPracticeAreas()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (practiceAreas) => {
          this.practiceAreas = practiceAreas;
          // Re-apply what is actually in the search box; the term survives a reload, so filtering
          // on anything else here would leave the list unfiltered while the box still showed it.
          this.applyFilter(this.searchQuery);
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading practice areas:', error);
          this.error = 'Failed to load practice areas. Please try again.';
          this.isLoading = false;
        },
      });
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  }

  getPracticeAreaInitials(name: string | null | undefined): string {
    if (!name) {
      return 'PA';
    }
    const trimmed = name.trim();
    if (trimmed.length === 0) {
      return 'PA';
    }
    const words = trimmed.split(/\s+/).filter(Boolean);
    if (words.length >= 2) {
      return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
    }
    const firstTwo = (words[0] || trimmed).slice(0, 2).toUpperCase();
    return firstTwo;
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort() ?? null;

    // Deferred a tick so enabling animations does not change a value already checked
    // in this change-detection pass. Left off entirely when the user has asked for reduced
    // motion, which also covers the expand/collapse trigger.
    if (!this.prefersReducedMotion()) {
      Promise.resolve().then(() => (this.animationsEnabled = true));
    }
  }

  private prefersReducedMotion(): boolean {
    return (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
  }

  private applySortToFiltered(): void {
    this.filteredPracticeAreas.sort((a, b) => {
      const aValue = this.getSortValue(a, this.sortField);
      const bValue = this.getSortValue(b, this.sortField);

      if (aValue < bValue) {
        return this.sortDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return this.sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }

  sortPracticeAreas(sort: Sort): void {
    this.sortField = sort.active;
    this.sortDirection = sort.direction as 'asc' | 'desc';

    this.applySortToFiltered();
    this.updateDataSource();
  }

  private getSortValue(practiceArea: PracticeArea, field: string): any {
    switch (field) {
      case 'practiceCode':
        return practiceArea.practiceCode.toLowerCase();
      case 'name':
      case 'description':
        return practiceArea.description.toLowerCase();
      default:
        return '';
    }
  }

  navigateToDetails(practiceAreaCode: string): void {
    this.router.navigate(['/admin/practice-areas', practiceAreaCode]);
  }

  onSearchInput(): void {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
    this.searchDebounceTimer = setTimeout(() => {
      this.applyFilter(this.searchQuery);
    }, 300);
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.applyFilter('');
  }

  // --- Inline create ---

  /**
   * Mirrors the backend's Admin.PracticeArea.Write gate on the create endpoint. Read on each
   * change-detection pass rather than cached at construction, so a mid-session role refresh — or
   * a logout clearing user state — is reflected without navigating away and back. This is how the
   * other admin screens resolve the same permission.
   */
  get canCreatePracticeArea(): boolean {
    return this.authService.canManagePracticeAreas();
  }

  /**
   * Whether the last create attempt was rejected — drives the red border and `aria-invalid`.
   * Derived from the message rather than tracked alongside it, so the border and what a screen
   * reader is told cannot fall out of step.
   */
  get hasCreateError(): boolean {
    return this.createErrorMessage !== null;
  }

  /** Whether the entered name is submittable; the API owns the uniqueness rule. */
  get canSubmitNewPracticeArea(): boolean {
    const name = this.newPracticeAreaName.trim();
    return name.length > 0 && name.length <= this.maxNameLength;
  }

  startCreate(): void {
    if (this.isCreating) return;

    this.isCreating = true;
    this.newPracticeAreaName = '';
    this.clearCreateError();
    this.focusNameInput();
  }

  /**
   * Refused while a create is in flight. The request is deliberately not aborted, so "cancel"
   * could not actually cancel anything — and allowing the control to close mid-save meant a late
   * response could no longer tell whether the control on screen was the one that issued it.
   * Keeping the control open until the response lands makes that unambiguous.
   */
  cancelCreate(): void {
    if (this.isSavingNewPracticeArea) return;

    this.isCreating = false;
    this.newPracticeAreaName = '';
    this.clearCreateError();

    // Collapsing destroys the focused field, which would drop focus to the document body and send
    // the next Tab back to the top of the page. Hand it back to the trigger the user came from.
    this.focusCreateTrigger();
  }

  /**
   * Drops the error state as soon as the user starts changing the rejected name — including a
   * wiggle that has not finished, so the control cannot still be shaking about an error the UI has
   * just withdrawn.
   */
  onNewPracticeAreaInput(): void {
    this.clearCreateError();
  }

  /** Clears every part of the error state, including a wiggle that has not finished. */
  private clearCreateError(): void {
    this.createErrorMessage = null;
    this.isWiggling = false;

    if (this.wiggleFrame !== null) {
      cancelAnimationFrame(this.wiggleFrame);
      this.wiggleFrame = null;
    }
  }

  private focusNameInput(): void {
    this.focusAfterRender(() => this.newPracticeAreaInput()?.nativeElement.focus());
  }

  private focusCreateTrigger(): void {
    this.focusAfterRender(() => this.createTrigger()?.nativeElement.focus());
  }

  /** The element being focused is swapped in by the expand animation, so it exists next tick. */
  private focusAfterRender(focus: () => void): void {
    if (this.focusTimer) {
      clearTimeout(this.focusTimer);
    }

    this.focusTimer = setTimeout(focus);
  }

  /**
   * `animationend` bubbles, so a Material ripple finishing on one of the action buttons would
   * otherwise cut the wiggle short. Matched on identity rather than `animationName`: Angular
   * scopes component keyframes, so the name arrives prefixed with the component's scope id and
   * never equals the literal written in the stylesheet.
   */
  onWiggleEnd(event: AnimationEvent): void {
    if (event.target === event.currentTarget) {
      this.isWiggling = false;
    }
  }

  /** Enter confirms, Escape cancels — matching the details page's inline rename. */
  onNewPracticeAreaKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.createPracticeArea();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelCreate();
    }
  }

  createPracticeArea(): void {
    if (this.isSavingNewPracticeArea) return;

    // Enter is the only way to reach this with an unsubmittable name — the confirm button is
    // disabled, which also suppresses its tooltip. Without this the keyboard path fails silently,
    // so reuse the same rejection feedback the API's own refusals get.
    if (!this.canSubmitNewPracticeArea) {
      const message = 'Enter a name for the new practice area.';
      this.snackbarService.showError(message);
      this.flagCreateError(message);
      return;
    }

    this.isSavingNewPracticeArea = true;
    this.clearCreateError();

    const name = this.newPracticeAreaName.trim();

    // Deliberately not tied to destroy$: aborting this POST after the server has accepted it
    // would create a practice area the user is never told about, then reject their retry as a
    // duplicate. The outcome is always announced through the toast — a root-level service, so it
    // survives this component — and only the in-place UI work is skipped once the control is gone.
    this.practiceAreaService.createPracticeArea(name).subscribe({
      next: (practiceArea) => {
        this.isSavingNewPracticeArea = false;
        this.isCreating = false;
        this.newPracticeAreaName = '';

        // The control cannot be closed while a create is in flight, so the only way to arrive
        // here without it on screen is having navigated away. Announce it rather than leaving the
        // user to discover it by hitting a duplicate-name error later; returning to the list
        // remounts this component and reloads, so the new row shows up on its own.
        if (this.isDestroyed) {
          this.snackbarService.showSuccess(`Practice area '${name}' was created.`);
          return;
        }

        this.navigateToDetails(practiceArea.practiceCode);
      },
      error: (error) => {
        this.isSavingNewPracticeArea = false;

        const message =
          describeApiError(error, 'Failed to create practice area. Please try again.');
        this.snackbarService.showError(message);

        if (this.isDestroyed) return;

        this.flagCreateError(message);

        // Disabling the input while saving blurs it, so return focus for the retry — otherwise
        // the corrected name is typed into nothing and the red border, which clears on input, has
        // no way to clear.
        this.focusNameInput();
      },
    });
  }

  /**
   * Reddens the control, wiggles it, and records `message` as the field's rejection reason for
   * assistive tech. Pass the same text the toast is given, so the two cannot disagree.
   *
   * Replaying the animation needs the browser to actually observe the class absent, which a
   * `setTimeout(0)` does not guarantee — the removal and re-add can be coalesced into one style
   * recalculation, so a second rejection would produce no motion at all. Two nested
   * `requestAnimationFrame`s put a real frame boundary in between.
   */
  private flagCreateError(message: string): void {
    this.createErrorMessage = message;
    this.isWiggling = false;

    if (this.wiggleFrame !== null) {
      cancelAnimationFrame(this.wiggleFrame);
      this.wiggleFrame = null;
    }

    // Under reduced motion no animation runs, so nothing would ever dispatch the `animationend`
    // that clears this flag — leaving it stuck true for exactly the users who opted out of
    // motion. Never raise it instead. The red border still carries the rejection.
    if (this.prefersReducedMotion()) {
      return;
    }

    this.wiggleFrame = requestAnimationFrame(() => {
      this.wiggleFrame = requestAnimationFrame(() => {
        this.wiggleFrame = null;
        this.isWiggling = true;
      });
    });
  }
}
